// src/contexts/AuthContext.js
import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { initializeAuth, logout as authLogout } from "../auth/auth";
import { loadDanceStyles } from "../config/danceStyles";
import { organizationApi } from "../api/organization";
import { toast } from "react-toastify";

const AuthContext = createContext();

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error("useAuth must be used within an AuthProvider");
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isInitialized, setIsInitialized] = useState(false);
    // Org profile (incl. settings.features) hydrated from the org-by-id endpoint after login.
    // Single source of truth for the feature-toggle visibility layer (dashboard cards, routes,
    // settings accordion). organisation is null until the fetch resolves; organisationLoading
    // gates the brand-new-org wizard trigger so it doesn't flash before features load.
    const [organisation, setOrganisation] = useState(null);
    const [organisationLoading, setOrganisationLoading] = useState(false);

    useEffect(() => {
        const checkAuth = async () => {
            setLoading(true);
            try {
                const userData = await initializeAuth();
                setUser(userData);
            } catch (error) {
                console.error("Failed to initialize auth:", error);
                setUser(null);
            } finally {
                setLoading(false);
                setIsInitialized(true);
            }
        };
        checkAuth();
    }, []);

    // Warm the canonical dance-styles cache once on app init (public endpoint,
    // independent of auth). Fire-and-forget: pickers read from the bundled
    // fallback until this resolves, so a failure is non-blocking.
    useEffect(() => {
        loadDanceStyles();
    }, []);

    useEffect(() => {
        const handleStorageChange = (e) => {
            if (e.key === 'accessToken' && e.newValue && e.newValue !== e.oldValue) {
                toast.warn(
                    'Login session changed in another tab — your permissions may be wrong. Refresh this tab to fix it.',
                    { autoClose: false, toastId: 'session-changed' }
                );
            }
        };
        window.addEventListener('storage', handleStorageChange);
        return () => window.removeEventListener('storage', handleStorageChange);
    }, []);

    // Fetch the org profile (with settings.features) for the active org. Exposed as
    // refreshOrganisation() so Settings/wizard can re-hydrate cards + nav + routes after a
    // PATCH /features without a page reload.
    const refreshOrganisation = useCallback(async () => {
        const orgId = user?.organisationId;
        if (!orgId) {
            setOrganisation(null);
            return null;
        }
        setOrganisationLoading(true);
        try {
            const org = await organizationApi.getOrgProfile(orgId);
            setOrganisation(org);
            return org;
        } catch (error) {
            console.error("Failed to load organisation profile:", error);
            return null;
        } finally {
            setOrganisationLoading(false);
        }
    }, [user?.organisationId]);

    // Hydrate the org profile whenever the active org changes (login, org switch).
    useEffect(() => {
        if (user?.organisationId) {
            refreshOrganisation();
        } else {
            setOrganisation(null);
        }
    }, [user?.organisationId, refreshOrganisation]);

    const logout = () => {
        authLogout(); // This removes tokens
        setUser(null);
        setOrganisation(null);
    };

    const login = userData => {
        setUser(userData);
    };

    /**
     * Whether a feature toggle is ON for the active org. Defaults to TRUE for any missing
     * key (and while the org is still loading or has no features object yet) — only an
     * EXPLICIT false hides a feature. Mirrors the backend fillFeatures() semantics so the
     * UI never wrongly hides a surface during hydration.
     */
    const checkFeature = useCallback((featureKey) => {
        return organisation?.settings?.features?.[featureKey] !== false;
    }, [organisation]);

    /**
     * Check whether the current user has a specific capability.
     *
     * loginType values:
     *   "owner"      — Direct self-login session. Applies to ALL account types: students,
     *                  instructors, and school owners. Despite the name it does NOT mean
     *                  "organisation owner". These users bypass capability checks because
     *                  their access is controlled by route-level guards, not JWT capabilities.
     *   "orgContext" — An org admin who has switched into managing a different organisation.
     *                  Capabilities are restricted to the role granted by that org (owner/admin/helper).
     *
     * Students with loginType "owner" pass this check but have no org-specific routes to access.
     */
    const checkPermission = useCallback((capability) => {
        if (!user) return false;
        if (user.loginType === "owner") return true;
        // Tokens issued before loginType was added won't have it — treat as owner for backward compat
        if (user.loginType !== "orgContext") return true;
        return user?.permissions?.capabilities?.[capability] === true;
    }, [user]);

    return (
        <AuthContext.Provider
            value={{
                user,
                setUser,
                loading,
                isInitialized,
                logout,
                login,
                isAuthenticated: !!user,
                accountDetails: user?.accountDetails || null,
                organisationId: user?.organisationId || null,
                loginType: user?.loginType || null,
                checkPermission,
                organisation,
                organisationLoading,
                refreshOrganisation,
                checkFeature,
            }}>
            {children}
        </AuthContext.Provider>
    );
};
